/**
 * SiteHeader — Redesigned premium header.
 * Inspired by Aesop/Apple: clean, minimal, with subtle scroll transitions.
 * Integrates global SearchModal (Cmd+K), Wishlist, and Admin Account Navigation.
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Package, Search, ShoppingBag, User, Menu, Shield } from "lucide-react";
import { useCart } from "@/features/cart/store/cart-store";
import { mainNav } from "@/config/navigation";
import { MobileNav } from "@/components/layout/mobile-nav";
import { SearchModal } from "@/features/search/components/search-modal";
import { useCurrentUser } from "@/hooks/use-auth";

import logoImg from "@/assets/logo.webp";

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      // Batch the layout read into one rAF per frame instead of reading
      // getBoundingClientRect() on every scroll event.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const heroEl = document.getElementById("interactive-hero-section");
        if (heroEl) {
          setScrolled(heroEl.getBoundingClientRect().bottom <= 80);
        } else {
          setScrolled(window.scrollY > 24);
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Global Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 bg-white border-b border-stone-200/90 shadow-sm">
        <div className="relative section-container flex items-center justify-between py-2 sm:py-3 px-3 sm:px-6">
          {/* Left Group: Mobile Hamburger, Desktop Nav, Search */}
          <div className="flex items-center gap-1.5 sm:gap-4 z-10">
            {/* Mobile Hamburger Menu Button (hidden on laptop/desktop) */}
            <button
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-sm text-stone-800 transition-colors hover:text-emerald-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800 md:hidden"
            >
              <Menu className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
            </button>

            {/* Desktop Navigation Bar Links (visible on tablet/laptop/PC) */}
            <nav className="hidden md:flex items-center gap-5 lg:gap-7">
              {mainNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-[11px] lg:text-xs uppercase tracking-[0.14em] font-semibold text-stone-700 hover:text-emerald-800 transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-800"
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search (Cmd+K)"
              title="Search (Cmd+K)"
              className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-sm text-stone-800 transition-colors hover:text-emerald-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
            >
              <Search className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
            </button>
          </div>

          {/* Center: Brand Logo — Crisp & Prominent on White */}
          {/* Center: Brand Logo — Balanced & Elegant Sizing */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center pointer-events-auto transition-transform duration-300 hover:scale-[1.02] rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-800"
            aria-label="Aster Decorz — Home"
          >
            <img
              src={logoImg}
              alt="Aster Decorz"
              width={350}
              height={118}
              loading="eager"
              fetchPriority="high"
              className="h-10 sm:h-14 md:h-16 w-auto max-w-[190px] sm:max-w-[280px] md:max-w-[350px] object-contain drop-shadow-sm"
            />
          </Link>

          {/* Right Icon Group: Orders, Cart Bag, Account */}
          <div className="flex items-center gap-1 sm:gap-2.5 z-10">
            {/* Orders Tracking Link */}
            <Link
              to="/orders"
              aria-label="My Orders & Tracking"
              title="My Orders & Tracking"
              className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-sm text-foreground transition-colors hover:text-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
            >
              <Package className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
            </Link>

            {/* Cart Bag Button */}
            <button
              aria-label={`Shopping bag, ${count} items`}
              onClick={() => setOpen(true)}
              className="relative grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-sm text-foreground transition-colors hover:text-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
            >
              <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4.5 w-4.5 place-items-center rounded-full bg-emerald-800 text-[10px] font-bold text-white tabular-nums shadow-sm">
                  {count}
                </span>
              )}
            </button>

            {/* Account Link */}
            <Link
              to={isAuthenticated ? "/account" : "/account/login"}
              aria-label="My Account"
              title="My Account"
              className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-sm text-foreground transition-colors hover:text-emerald-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
            >
              <User className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile drawer nav */}
      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />

      {/* Intelligent Search Modal */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
